// src/data/formTemplates.ts
//
// Bibliothèque de modèles de formulaires dynamiques (FormBuilder) prêts à
// charger pour les types d'opération courants. Chaque modèle est un objet
// `FormBuilderConfig` (cf. workflow.types.ts) avec une liste de champs.
//
// L'admin peut :
//  - charger un modèle tel quel (remplace tous les champs existants)
//  - charger un modèle puis l'éditer (ajouter, retirer, ajuster validations)
//
// Convention de codes :
//  - snake_case minuscules (ex: 'montant_total', 'date_demande')
//  - les codes système suggérés sont préservés à travers les modèles
//    (ex: 'montant', 'beneficiaire', 'motif') pour favoriser la cohérence
//    inter-types.

import type { FormBuilderConfig, FormBuilderChamp } from '../types/workflow.types';

/**
 * Type de modèle de formulaire :
 *  - 'simple'    : configuration FormBuilder mono-étape (`{ champs: [...] }`)
 *                  → "Charger" remplace les champs de l'étape en cours d'édition.
 *  - 'composite' : composant React multi-étapes pré-existant (ex: formulaire
 *                  d'adhésion avec stepper, signature, upload). Pas chargeable
 *                  dans le FormBuilder simple — à la place, le bouton devient
 *                  "Lancer la saisie" et navigue vers `composite.route`.
 */
export type FormTemplateKind = 'simple' | 'composite';

export interface FormTemplate {
  /** Identifiant technique unique */
  code: string;
  /** Libellé affiché dans la bibliothèque */
  label: string;
  /** Catégorie pour le regroupement (ex: 'Trésorerie', 'RH', 'Achats') */
  categorie: string;
  /** Description / cas d'usage */
  description: string;
  /** Codes des types d'opération SAGeCom auxquels ce modèle s'applique
   *  typiquement (suggestion seulement — l'admin peut charger n'importe
   *  quel modèle sur n'importe quel type). */
  suggestedTypeOperations: string[];
  /** Configuration du formulaire (champs FormBuilder).
   *  Pour les modèles `kind: 'composite'`, peut être vide ou contenir un
   *  squelette descriptif (les vrais champs sont gérés par le composant). */
  config: FormBuilderConfig;
  /** Type de modèle. Défaut : 'simple' (rétro-compatible). */
  kind?: FormTemplateKind;
  /** Métadonnées pour les modèles composite (composant React multi-étapes). */
  composite?: {
    /** Route à naviguer pour lancer la saisie (ex: '/adhesions/nouvelle'). */
    route: string;
    /** Nombre d'étapes (informatif, affiché sur la carte). */
    nbEtapes?: number;
    /** Liste de tags pour montrer les capacités (ex: ['stepper', 'signature']). */
    capacites?: string[];
    /**
     * Codes des modules SAGeCom (catalogue) où ce composite doit apparaître
     * automatiquement dans le sidebar (au-dessus des types DevMode). Permet
     * d'afficher "Nouvelle adhésion" sous "Gestion des Adhérents" sans coder
     * le lien à la main pour chaque module.
     */
    moduleCodes?: string[];
    /** Icône Lucide pour le sidebar. Défaut : Layers. */
    icon?: string;
  };
}

/** Retourne les composites visibles dans le sidebar pour un module donné. */
export function getCompositesForModule(moduleCode: string): FormTemplate[] {
  return FORM_TEMPLATES.filter(t =>
    t.kind === 'composite' &&
    Array.isArray(t.composite?.moduleCodes) &&
    t.composite!.moduleCodes!.includes(moduleCode),
  );
}

// ── Helper pour créer un champ ──────────────────────────────────────────────
const champ = (
  code: string,
  label: string,
  type: string,
  options: Partial<FormBuilderChamp> = {},
  ordreOffset = 0,
): FormBuilderChamp => ({
  code,
  label,
  type,
  required: false,
  ordre: 10 + ordreOffset * 10,
  ...options,
});

// ── Modèles ─────────────────────────────────────────────────────────────────
export const FORM_TEMPLATES: FormTemplate[] = [

  // ── Trésorerie : Note de frais ─────────────────────────────────────────────
  {
    code: 'note_frais',
    label: 'Note de frais',
    categorie: 'Trésorerie',
    description: 'Remboursement de frais professionnels avancés par un employé (transport, restauration, hébergement).',
    suggestedTypeOperations: ['DECAISSEMENT_CHARGE', 'NOTE_FRAIS'],
    config: {
      champs: [
        champ('montant',         'Montant total',           'montant',  { required: true,  validation: { min: 0 } }, 0),
        champ('motif',           'Motif',                   'textarea', { required: true,  validation: { maxLength: 500 } }, 1),
        champ('date_engagement', 'Date d\'engagement',       'date',     { required: true }, 2),
        champ('beneficiaire',    'Bénéficiaire',            'text',     { required: true }, 3),
        champ('categorie_frais', 'Catégorie de frais',      'select',   {
          required: true,
          options: [
            { value: 'transport',     label: 'Transport' },
            { value: 'restauration',  label: 'Restauration' },
            { value: 'hebergement',   label: 'Hébergement' },
            { value: 'fourniture',    label: 'Fournitures' },
            { value: 'communication', label: 'Communication' },
            { value: 'autre',         label: 'Autre' },
          ],
        }, 4),
        champ('justificatif',    'Justificatif (facture)',  'file',     { required: true, help: 'Format PDF ou image' }, 5),
      ],
    },
  },

  // ── Trésorerie : Paiement fournisseur ─────────────────────────────────────
  {
    code: 'paiement_fournisseur',
    label: 'Paiement fournisseur',
    categorie: 'Trésorerie',
    description: 'Règlement d\'une facture fournisseur (avec ou sans retenue à la source).',
    suggestedTypeOperations: ['DECAISSEMENT_FOURNISSEUR', 'paiement_fournisseur'],
    config: {
      champs: [
        champ('fournisseur_id',     'Fournisseur',                'reference',{ required: true,  help: 'Liste des fournisseurs actifs' }, 0),
        champ('reference_facture',  'Référence facture',          'text',     { required: true,  validation: { maxLength: 50 } }, 1),
        champ('montant_ht',         'Montant HT',                 'montant',  { required: true,  validation: { min: 0 } }, 2),
        champ('taux_tva',           'Taux TVA (%)',               'number',   { required: false, defaultValue: 18, validation: { min: 0, max: 100 } }, 3),
        champ('montant_ttc',        'Montant TTC',                'montant',  { required: true,  validation: { min: 0 } }, 4),
        champ('ras_appliquee',      'Retenue à la source ?',      'checkbox', {}, 5),
        champ('mode_paiement',      'Mode de paiement',           'select',   {
          required: true,
          options: [
            { value: 'virement',     label: 'Virement bancaire' },
            { value: 'cheque',       label: 'Chèque' },
            { value: 'especes',      label: 'Espèces' },
            { value: 'mobile_money', label: 'Mobile Money' },
          ],
        }, 6),
        champ('date_echeance',      'Date d\'échéance',          'date',     { required: false }, 7),
        champ('facture_pdf',        'Facture (PDF)',              'file',     { required: true }, 8),
      ],
    },
  },

  // ── Trésorerie : Paiement loyer ────────────────────────────────────────────
  {
    code: 'paiement_loyer',
    label: 'Paiement de loyer',
    categorie: 'Trésorerie',
    description: 'Règlement mensuel d\'un loyer (immobilier, équipement).',
    suggestedTypeOperations: ['paiement_loyer', 'DECAISSEMENT_CHARGE'],
    config: {
      champs: [
        champ('bailleur',           'Bailleur',                   'text',     { required: true }, 0),
        champ('contrat_bail',       'N° contrat de bail',         'text',     { required: false }, 1),
        champ('mois_concerne',      'Mois concerné',              'select',   {
          required: true,
          options: [
            { value: '01', label: 'Janvier' }, { value: '02', label: 'Février' }, { value: '03', label: 'Mars' },
            { value: '04', label: 'Avril' }, { value: '05', label: 'Mai' }, { value: '06', label: 'Juin' },
            { value: '07', label: 'Juillet' }, { value: '08', label: 'Août' }, { value: '09', label: 'Septembre' },
            { value: '10', label: 'Octobre' }, { value: '11', label: 'Novembre' }, { value: '12', label: 'Décembre' },
          ],
        }, 2),
        champ('annee_concernee',    'Année',                      'number',   { required: true,  validation: { min: 2020, max: 2100 } }, 3),
        champ('montant_loyer',      'Montant loyer',              'montant',  { required: true,  validation: { min: 0 } }, 4),
        champ('charges',            'Charges (eau, électricité)', 'montant',  { required: false, validation: { min: 0 } }, 5),
        champ('date_paiement',      'Date de paiement',           'date',     { required: true }, 6),
      ],
    },
  },

  // ── Trésorerie : Remboursement trop-perçu ─────────────────────────────────
  {
    code: 'remboursement_trop_percu',
    label: 'Remboursement trop-perçu',
    categorie: 'Trésorerie',
    description: 'Remboursement d\'un montant excédentaire reçu d\'un client ou versé à un employé.',
    suggestedTypeOperations: ['REMBOURSEMENT_DES_TROP_PERCU'],
    config: {
      champs: [
        champ('beneficiaire',       'Bénéficiaire',               'text',     { required: true }, 0),
        champ('type_beneficiaire',  'Type de bénéficiaire',       'select',   {
          required: true,
          options: [
            { value: 'salarie',     label: 'Salarié' },
            { value: 'client',      label: 'Client' },
            { value: 'fournisseur', label: 'Fournisseur' },
            { value: 'autre',       label: 'Autre' },
          ],
        }, 1),
        champ('montant',            'Montant à rembourser',       'montant',  { required: true,  validation: { min: 0 } }, 2),
        champ('motif',              'Motif du trop-perçu',        'textarea', { required: true,  validation: { maxLength: 500 } }, 3),
        champ('reference_origine',  'Référence du paiement initial', 'text',  { required: false, help: 'N° de la transaction d\'origine' }, 4),
        champ('date_origine',       'Date du paiement initial',   'date',     { required: false }, 5),
        champ('date_remboursement', 'Date du remboursement',      'date',     { required: true }, 6),
        champ('justificatif',       'Justificatif',               'file',     { required: true,  help: 'Document attestant du trop-perçu' }, 7),
      ],
    },
  },

  // ── RH : Demande d'avance / acompte ─────────────────────────────────────────
  {
    code: 'avance_acompte_salaire',
    label: 'Avance / acompte sur salaire',
    categorie: 'RH',
    description: 'Demande d\'avance sur salaire à régulariser au prochain bulletin de paie.',
    suggestedTypeOperations: ['acompte_salaire', 'avance'],
    config: {
      champs: [
        champ('employe_matricule',  'Matricule employé',          'text',     { required: true }, 0),
        champ('employe_nom',        'Nom complet',                'text',     { required: true }, 1),
        champ('montant',            'Montant demandé',            'montant',  { required: true,  validation: { min: 0 } }, 2),
        champ('motif',              'Motif',                      'textarea', { required: true,  validation: { maxLength: 300 } }, 3),
        champ('mois_regularisation', 'Mois de régularisation',    'date',     { required: true,  help: 'Mois où l\'avance sera déduite' }, 4),
        champ('mode_paiement',      'Mode de paiement',           'select',   {
          required: true,
          options: [
            { value: 'virement',     label: 'Virement' },
            { value: 'especes',      label: 'Espèces' },
            { value: 'mobile_money', label: 'Mobile Money' },
          ],
        }, 5),
      ],
    },
  },

  // ── RH : Demande de congé ───────────────────────────────────────────────────
  {
    code: 'demande_conge',
    label: 'Demande de congé',
    categorie: 'RH',
    description: 'Demande de congé payé/maladie/maternité avec dates et solde restant.',
    suggestedTypeOperations: ['conge'],
    config: {
      champs: [
        champ('employe_matricule', 'Matricule employé',           'text',     { required: true }, 0),
        champ('type_conge',        'Type de congé',               'select',   {
          required: true,
          options: [
            { value: 'paye',       label: 'Congé payé' },
            { value: 'maladie',    label: 'Congé maladie' },
            { value: 'maternite',  label: 'Maternité' },
            { value: 'paternite',  label: 'Paternité' },
            { value: 'sans_solde', label: 'Sans solde' },
            { value: 'exceptionnel', label: 'Exceptionnel' },
          ],
        }, 1),
        champ('date_debut',        'Date de début',               'date',     { required: true }, 2),
        champ('date_fin',          'Date de fin',                 'date',     { required: true }, 3),
        champ('nb_jours',          'Nombre de jours',             'number',   { required: true,  validation: { min: 1, max: 365 } }, 4),
        champ('motif',             'Motif',                       'textarea', { required: false, validation: { maxLength: 300 } }, 5),
        champ('justificatif',      'Justificatif (si maladie)',  'file',     { required: false }, 6),
        champ('contact_urgence',   'Contact d\'urgence',         'tel',      { required: false }, 7),
      ],
    },
  },

  // ── Achats : Bon de commande fournisseur ──────────────────────────────────
  {
    code: 'bon_commande',
    label: 'Bon de commande fournisseur',
    categorie: 'Achats',
    description: 'Émission d\'un bon de commande à un fournisseur (avant facture).',
    suggestedTypeOperations: ['bon_commande', 'achats'],
    config: {
      champs: [
        champ('fournisseur_id',     'Fournisseur',                'reference',{ required: true }, 0),
        champ('reference_bc',       'N° du bon de commande',      'text',     { required: true }, 1),
        champ('date_commande',      'Date de la commande',        'date',     { required: true }, 2),
        champ('description',        'Description des produits',   'textarea', { required: true,  validation: { maxLength: 1000 } }, 3),
        champ('montant_estime',     'Montant estimé',             'montant',  { required: true,  validation: { min: 0 } }, 4),
        champ('date_livraison',     'Date de livraison souhaitée','date',     { required: false }, 5),
        champ('lieu_livraison',     'Lieu de livraison',          'text',     { required: false }, 6),
        champ('contact_fournisseur','Contact fournisseur',        'tel',      { required: false }, 7),
      ],
    },
  },

  // ── Multi-bénéficiaires : Bordereau main d'œuvre ──────────────────────────
  {
    code: 'bordereau_main_oeuvre',
    label: 'Bordereau main-d\'œuvre',
    categorie: 'RH',
    description: 'Paiement collectif à plusieurs bénéficiaires (équipe sur chantier, intérimaires).',
    suggestedTypeOperations: ['bordereau_main_oeuvre', 'paiement_masse'],
    config: {
      champs: [
        champ('chantier',           'Chantier / projet',          'text',     { required: true }, 0),
        champ('periode_debut',      'Début de période',           'date',     { required: true }, 1),
        champ('periode_fin',        'Fin de période',             'date',     { required: true }, 2),
        champ('nb_beneficiaires',   'Nombre de bénéficiaires',    'number',   { required: true,  validation: { min: 1, max: 200 } }, 3),
        champ('montant_total',      'Montant total',              'montant',  { required: true,  validation: { min: 0 } }, 4),
        champ('liste_beneficiaires','Liste détaillée (Excel)',    'file',     { required: true,  help: 'Format Excel : colonnes Nom, Matricule, Montant' }, 5),
        champ('responsable_chantier','Responsable du chantier',  'text',      { required: true }, 6),
        champ('observations',       'Observations',               'textarea', { required: false, validation: { maxLength: 500 } }, 7),
      ],
    },
  },

  // ── Caisse : Versement banque ─────────────────────────────────────────────
  {
    code: 'versement_banque',
    label: 'Versement bancaire',
    categorie: 'Caisse',
    description: 'Remise d\'espèces à la banque (bordereau de remise).',
    suggestedTypeOperations: ['versement_banque'],
    config: {
      champs: [
        champ('banque_id',          'Banque destinataire',        'reference',{ required: true }, 0),
        champ('numero_bordereau',   'N° de bordereau',            'text',     { required: false, help: 'Auto-généré si vide' }, 1),
        champ('montant',            'Montant remis',              'montant',  { required: true,  validation: { min: 0 } }, 2),
        champ('date_remise',        'Date de remise',             'date',     { required: true }, 3),
        champ('caissier',           'Caissier',                   'text',     { required: true }, 4),
        champ('observations',       'Observations',               'textarea', { required: false }, 5),
      ],
    },
  },

  // ── Caisse : Apport / entrée de fonds ─────────────────────────────────────
  {
    code: 'apport_fonds',
    label: 'Apport / entrée de fonds',
    categorie: 'Caisse',
    description: 'Approvisionnement initial de caisse, don, subvention, dîme, offrande.',
    suggestedTypeOperations: ['ENTREE_FONDS'],
    config: {
      champs: [
        champ('nature',             'Nature de l\'entrée',       'select',   {
          required: true,
          options: [
            { value: 'approvisionnement',  label: 'Approvisionnement' },
            { value: 'don',                label: 'Don' },
            { value: 'subvention',         label: 'Subvention' },
            { value: 'apport_capital',     label: 'Apport au capital' },
            { value: 'caution_recue',      label: 'Caution reçue' },
            { value: 'remboursement_recu', label: 'Remboursement reçu' },
            { value: 'avance_recue',       label: 'Avance reçue' },
            { value: 'autre',              label: 'Autre' },
          ],
        }, 0),
        champ('montant',            'Montant',                    'montant',  { required: true,  validation: { min: 0 } }, 1),
        champ('source',             'Source / émetteur',          'text',     { required: true }, 2),
        champ('motif',              'Motif',                      'textarea', { required: false, validation: { maxLength: 500 } }, 3),
        champ('date_entree',        'Date d\'entrée',            'date',     { required: true }, 4),
        champ('justificatif',       'Justificatif',               'file',     { required: false, help: 'Reçu, contrat, attestation…' }, 5),
      ],
    },
  },

  // ── Patronat — Adhésion (composite multi-étapes) ──────────────────────────
  // Ces 3 modèles ne sont PAS chargeables dans le FormBuilder simple : ils
  // ouvrent un composant React multi-étapes complet (stepper, validation,
  // signature électronique, upload de pièces, etc.). Le bouton "Charger"
  // devient "Lancer la saisie" et navigue vers la route back-office.
  {
    code: 'adhesion_patronale',
    label: 'Adhésion patronale (multi-types)',
    categorie: 'Patronat — Adhésion',
    description:
      'Formulaire 5 étapes avec choix du profil (Entreprise / Fédération / EPA-Association). ' +
      'Identification → Marchés → Contacts → Activité → Signature. ' +
      'Soumission via workflow ADH_SION (déclenche le processus standard).',
    suggestedTypeOperations: ['ADH_SION', 'ADHESION'],
    kind: 'composite',
    composite: {
      route: '/adhesions/nouvelle',
      nbEtapes: 5,
      capacites: ['stepper', 'multi-profils', 'pieces-jointes', 'signature-electronique'],
      moduleCodes: ['gestion_adherents'],
      icon: 'UserPlus',
    },
    config: { champs: [] },
  },

  // ── Migration / Import de masse des adhérents existants ──────────────
  {
    code: 'migration_adherents',
    label: 'Migration des adhérents (Excel / BD / API)',
    categorie: 'Patronat — Adhésion',
    description:
      'Import de masse des adhérents historiques depuis un fichier Excel/CSV, ' +
      'une base de données externe ou une API. Mapping de colonnes, aperçu, ' +
      'validation, rapport détaillé. Cas d\'usage : migration depuis un système legacy.',
    suggestedTypeOperations: ['ADH_SION'],
    kind: 'composite',
    composite: {
      route: '/adhesions/migration',
      nbEtapes: 4,
      capacites: ['excel-csv', 'database', 'api-rest', 'mapping-colonnes', 'rapport-detaille'],
      moduleCodes: ['gestion_adherents'],
      icon: 'Upload',
    },
    config: { champs: [] },
  },

  // ── Patronat / Associations : Planification d'activité ────────────────────
  // Modèle riche couvrant la planification d'événements pour organisations
  // patronales (CGE-GUI, CGECI, etc.) : formations, séminaires, AG,
  // conférences, ateliers, gala, salons, missions de lobbying.
  {
    code: 'activite_patronale_planification',
    label: 'Planification d\'activité patronale',
    categorie: 'Activités patronales',
    description: 'Planifier une activité (formation, séminaire, AG, conférence, atelier, gala, salon, lobbying) avec lieu, public cible, capacité, budget et logistique.',
    suggestedTypeOperations: ['ACTIVITES_PATRONALES', 'GESTION_ACTIVITES', 'PLANIFICATION_ACTIVITE'],
    config: {
      champs: [
        // ── 1. Identification ────────────────────────────────────────────────
        champ('titre', 'Titre / Intitulé de l\'activité', 'text', {
          required: true,
          validation: { maxLength: 200 },
          help: 'Ex: Forum des PME 2026, Séminaire RSE, Assemblée Générale annuelle',
        }, 0),
        champ('type_activite', 'Type d\'activité', 'select', {
          required: true,
          options: [
            { value: 'formation',          label: 'Formation' },
            { value: 'seminaire',          label: 'Séminaire' },
            { value: 'atelier',            label: 'Atelier / Workshop' },
            { value: 'conference',         label: 'Conférence' },
            { value: 'assemblee_generale', label: 'Assemblée Générale' },
            { value: 'reunion_bureau',     label: 'Réunion du bureau' },
            { value: 'reunion_commission', label: 'Réunion de commission' },
            { value: 'gala',               label: 'Gala / Cérémonie' },
            { value: 'salon',              label: 'Salon / Foire' },
            { value: 'lobbying',           label: 'Mission de lobbying / Plaidoyer' },
            { value: 'visite_terrain',     label: 'Visite de terrain' },
            { value: 'forum',              label: 'Forum' },
            { value: 'webinaire',          label: 'Webinaire' },
            { value: 'autre',              label: 'Autre' },
          ],
        }, 1),
        champ('reference_interne', 'Référence interne', 'text', {
          help: 'Code projet, référence comptable… (optionnel)',
        }, 2),

        // ── 2. Planification temporelle ──────────────────────────────────────
        champ('date_debut', 'Date de début', 'date', { required: true }, 3),
        champ('heure_debut', 'Heure de début', 'time', {}, 4),
        champ('date_fin', 'Date de fin', 'date', {
          help: 'Laissez vide pour une activité sur une seule journée',
        }, 5),
        champ('heure_fin', 'Heure de fin', 'time', {}, 6),

        // ── 3. Localisation / Format ─────────────────────────────────────────
        champ('format', 'Format', 'select', {
          required: true,
          options: [
            { value: 'presentiel', label: 'Présentiel' },
            { value: 'distanciel', label: 'Distanciel (visioconférence)' },
            { value: 'hybride',    label: 'Hybride (présentiel + distanciel)' },
          ],
        }, 7),
        champ('lieu', 'Lieu / Salle', 'text', {
          help: 'Ex: nom de l\'hôtel, siège de l\'organisation, salle de conférence',
        }, 8),
        champ('adresse_complete', 'Adresse complète', 'textarea', {
          validation: { maxLength: 300 },
        }, 9),
        champ('lien_visio', 'Lien de visioconférence', 'text', {
          help: 'URL Zoom / Teams / Google Meet (si distanciel ou hybride)',
        }, 10),

        // ── 4. Public et inscriptions ────────────────────────────────────────
        champ('public_cible', 'Public cible', 'select', {
          required: true,
          options: [
            { value: 'membres_seulement',     label: 'Membres uniquement' },
            { value: 'dirigeants',            label: 'Dirigeants d\'entreprises membres' },
            { value: 'employes_membres',      label: 'Employés des entreprises membres' },
            { value: 'membres_et_partenaires', label: 'Membres + partenaires invités' },
            { value: 'institutions',          label: 'Institutions / autorités publiques' },
            { value: 'medias',                label: 'Médias / presse' },
            { value: 'tout_public',           label: 'Tout public' },
          ],
        }, 11),
        champ('capacite_max', 'Capacité maximale', 'number', {
          help: 'Nombre maximum de participants',
          validation: { min: 1 },
        }, 12),
        champ('inscription_requise', 'Inscription préalable requise', 'checkbox', {}, 13),
        champ('date_limite_inscription', 'Date limite d\'inscription', 'date', {}, 14),
        champ('contribution_participation', 'Frais de participation', 'montant', {
          help: 'Laisser vide ou 0 si gratuit',
          validation: { min: 0 },
        }, 15),

        // ── 5. Contenu et programme ──────────────────────────────────────────
        champ('description', 'Description', 'textarea', {
          required: true,
          validation: { maxLength: 2000 },
          help: 'Présentation générale, contexte, enjeux',
        }, 16),
        champ('objectifs', 'Objectifs', 'textarea', {
          validation: { maxLength: 1000 },
          help: 'Résultats attendus, livrables',
        }, 17),
        champ('intervenants', 'Intervenants / Animateurs', 'textarea', {
          validation: { maxLength: 1000 },
          help: 'Liste des intervenants prévus avec leur titre/affiliation',
        }, 18),
        champ('programme', 'Programme détaillé (PDF)', 'file', {
          help: 'Document du programme, agenda',
        }, 19),
        champ('affiche', 'Affiche / Visuel', 'file', {
          help: 'Image ou PDF de l\'affiche promotionnelle',
        }, 20),
        champ('partenaires', 'Partenaires / Sponsors', 'textarea', {
          validation: { maxLength: 500 },
          help: 'Liste des partenaires financiers et techniques',
        }, 21),

        // ── 6. Budget prévisionnel ───────────────────────────────────────────
        champ('budget_previsionnel', 'Budget prévisionnel total', 'montant', {
          validation: { min: 0 },
        }, 22),
        champ('source_financement', 'Source de financement', 'select', {
          options: [
            { value: 'cotisations',          label: 'Cotisations membres' },
            { value: 'sponsors',             label: 'Sponsors / partenaires' },
            { value: 'inscriptions',         label: 'Frais d\'inscription' },
            { value: 'subvention_publique',  label: 'Subvention publique' },
            { value: 'fonds_propres',        label: 'Fonds propres' },
            { value: 'mixte',                label: 'Mixte (combiné)' },
            { value: 'autre',                label: 'Autre' },
          ],
        }, 23),

        // ── 7. Logistique et contact ─────────────────────────────────────────
        champ('personne_contact', 'Personne contact', 'text', {
          help: 'Nom du référent organisateur',
        }, 24),
        champ('telephone_contact', 'Téléphone contact', 'text', {
          validation: { maxLength: 30 },
        }, 25),
        champ('email_contact', 'Email contact', 'text', {
          validation: { maxLength: 150 },
        }, 26),
        champ('observations', 'Observations / Remarques', 'textarea', {
          validation: { maxLength: 1000 },
          help: 'Précisions logistiques, prérequis, restrictions…',
        }, 27),
      ],
    },
  },

  // ── Modèle vide ────────────────────────────────────────────────────────────
  {
    code: 'vide',
    label: 'Modèle vide (à construire)',
    categorie: 'Générique',
    description: 'Démarrer avec un formulaire vierge. Vous ajouterez les champs à la main.',
    suggestedTypeOperations: [],
    config: { champs: [] },
  },
];

/** Retourne les modèles regroupés par catégorie. */
export function getTemplatesByCategorie(): Record<string, FormTemplate[]> {
  const grouped: Record<string, FormTemplate[]> = {};
  for (const t of FORM_TEMPLATES) {
    if (!grouped[t.categorie]) grouped[t.categorie] = [];
    grouped[t.categorie].push(t);
  }
  return grouped;
}

/** Retourne les modèles qui matchent le mieux un type d'opération donné. */
export function getSuggestedTemplates(typeOperationCode: string): FormTemplate[] {
  const code = typeOperationCode.toLowerCase();
  return FORM_TEMPLATES.filter(t =>
    t.suggestedTypeOperations.some(s =>
      s.toLowerCase() === code || code.includes(s.toLowerCase()),
    ),
  );
}
